/**
 * K-T-13-B — Layer-3 hook for canonical per-team daily capacity.
 *
 * Thin wrapper around `kpi_team_capacity(shop_id, p_day)`. Returns
 * one `TeamCapacityResult` per active team with capacity (canonical
 * sum across Active-employment members of `kpi_technician_capacity`),
 * today's booked minutes (Active+Paused tickets on the day), and the
 * tech / ticket counts the panel needs.
 *
 * Replaces the inline shift_start/shift_end parsing + 480 fallback +
 * local utilization math in `TeamCapacityCard.tsx`. The capacity
 * resolution chain (shifts > daily_capacity_minutes > 480 fallback,
 * with eff/buf multipliers) carries through verbatim from the K-T-2
 * canonical primitive.
 *
 * Utilization is computed by the caller (the widget caps at 100% for
 * visual fit; the RPC returns raw values to keep the primitive
 * neutral).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { TeamCapacityResult } from '../../../supabase/functions/_shared/kpiCanonical';

export interface UseTeamCapacityOptions {
  shopId: string;
  /** Shop-local `yyyy-MM-dd` for the target day. */
  day: string;
}

export interface UseTeamCapacityResult {
  data: TeamCapacityResult[] | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Stable query key:
 *   ['kpi', 'kpi_team_capacity', shopId, day]
 */
export function useTeamCapacity(
  opts: UseTeamCapacityOptions,
): UseTeamCapacityResult {
  const query = useQuery({
    queryKey: [
      'kpi',
      'kpi_team_capacity',
      opts.shopId,
      opts.day,
    ],
    queryFn: async (): Promise<TeamCapacityResult[] | null> => {
      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: { p_shop_id: string; p_day: string },
      ) => Promise<{
        data: TeamCapacityResult[] | null;
        error: unknown;
      }>)('kpi_team_capacity', {
        p_shop_id: opts.shopId,
        p_day: opts.day,
      });

      if (error) throw error;
      return data ?? [];
    },
    enabled: Boolean(opts.shopId && opts.day),
    staleTime: 60 * 1000,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
